import * as React from 'react'
import { useRouter } from 'next/router'

import { i18n } from '@thg-commerce/enterprise-core'
import { ParcelLabWidget } from '@thg-commerce/enterprise-widget-parcellab'

import { BaseWidgetProps } from '../types'

import { FormContainer, SearchButton, SearchForm, SearchInput } from './styles'

export interface ParcelLabWidgetRendererProps extends BaseWidgetProps {
  orderNumber?: string
  email?: string
  language?: string
  parcelLabUserId?: number
}

export const ParcelLabWidgetRenderer: React.FC<ParcelLabWidgetRendererProps> = ({
  orderNumber: initialOrderNumber = '',
  email: initialEmail = '',
  language = 'en',
  parcelLabUserId,
}) => {
  const router = useRouter()
  const { orderNo, zip } = router.query
  const [orderNumber, setOrderNumber] = React.useState(
    (orderNo as string) || initialOrderNumber,
  )
  const [zipCode, setZipCode] = React.useState((zip as string) || '')
  const [showTracking, setShowTracking] = React.useState(!!orderNo && !!zip)
  const [showSearchForm, setShowSearchForm] = React.useState(!showTracking)
  const isDisabled = orderNumber.trim() === '' || zipCode.trim() === ''
  const i18nText = {
    buttonTitle: i18n('widget.parcellab.track.button.title'),
  }

  React.useEffect(() => {
    if (!showTracking) return

    const checkForTrackingResults = () => {
      const trackingElement = document.querySelector(
        '[data-testid="track-and-trace"]',
      )
      if (trackingElement) {
        setShowSearchForm(false)
      }
    }

    const observer = new MutationObserver(checkForTrackingResults)

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    checkForTrackingResults()

    return () => {
      observer.disconnect()
    }
  }, [showTracking])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (orderNumber && zipCode) {
      setShowTracking(true)

      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, orderNo: orderNumber, zip: zipCode },
        },
        undefined,
        { shallow: true },
      )
    }
  }

  return (
    <div>
      {showSearchForm && (
        <FormContainer>
          <SearchForm onSubmit={handleSubmit}>
            <SearchInput
              type="text"
              placeholder="Order Number"
              value={orderNumber}
              onChange={(e) => {
                setOrderNumber(e.target.value)
                setShowTracking(false)
              }}
            />
            <SearchInput
              type="text"
              placeholder="ZIP Code"
              value={zipCode}
              onChange={(e) => {
                setZipCode(e.target.value)
                setShowTracking(false)
              }}
            />
            <SearchButton
              type="submit"
              emphasis="high"
              data-testid="track-order-button"
              disabled={isDisabled}
            >
              {i18nText.buttonTitle}
            </SearchButton>
          </SearchForm>
        </FormContainer>
      )}

      {showTracking && (
        <React.Fragment>
          <ParcelLabWidget
            orderNumber={orderNumber}
            zipCode={zipCode}
            email={initialEmail}
            language={language}
            parcelLabUserId={parcelLabUserId}
          />
        </React.Fragment>
      )}
    </div>
  )
}
